import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, CircularProgress,
  Alert, TextField, Paper, Chip, Divider, ToggleButtonGroup,
  ToggleButton, FormControlLabel, Switch, Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  Event as EventIcon,
  Place as PlaceIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import API_BASE_URL from '../../config/api';
import { API_ENDPOINTS } from '../../config/api';

async function apiGet(endpoint) {
  const res = await fetch(`${API_BASE_URL}${endpoint}`);
  return res.json();
}

async function apiPost(endpoint, body) {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

const STYLE_DEFAULTS = {
  bgColor: '#f5f5f0',
  textColor: '#2d2d2d',
  accentColor: '#1b331f',
  fontFamily: 'Playfair Display',
  headingSize: 30,
  bodySize: 15,
};

const TEXT_DEFAULTS = {
  greetingText: 'Szeretettel meghívjuk Önt az alábbi eseményre. Kérjük, jelezze vissza részvételét.',
  rsvpSectionTitle: 'Részt vesz?',
  attendingLabel: 'Részt veszek',
  declineLabel: 'Sajnos nem tudok jönni',
  maybeLabel: 'Talán',
  submitLabel: 'Visszajelzés küldése',
};

function loadGoogleFont(family) {
  const id = `gfont-${family.replace(/\s+/g, '-')}`;
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
  }
}

const pad2 = (n) => String(n).padStart(2, '0');

// RFC 5545 szerinti szövegkiszökés (vessző, pontosvessző, backslash, sortörés)
function escapeIcsText(str) {
  return String(str || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

// .ics naptárfájl összeállítása az esküvő adataiból, hogy a vendég a saját
// alapértelmezett naptárprogramjába (Google/Apple/Outlook) tudja importálni,
// telefonon és desktopon egyaránt — nincs szükség külső könyvtárra hozzá.
function buildInvitationIcs(eventData) {
  // eventDate a backend-ben @db.Date (csak dátum, UTC éjfélre normalizálva),
  // ezért a UTC komponensek adják a helyes naptári napot, függetlenül a
  // vendég böngészőjének időzónájától.
  const d = new Date(eventData.eventDate);
  const dateStr = `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}`;

  const location = [eventData.venueName, eventData.venueAddress, eventData.venueCity]
    .filter(Boolean)
    .join(', ');

  let dtStartLine;
  let dtEndOrDurationLine;
  const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(eventData.eventTime || '');
  if (timeMatch) {
    const hh = pad2(timeMatch[1]);
    const mm = pad2(timeMatch[2]);
    // TZID=Europe/Budapest: a legtöbb naptáralkalmazás beágyazott VTIMEZONE
    // nélkül is felismeri a szabványos Olson-azonosítót, így a helyi idő
    // helyesen jelenik meg attól függetlenül, hogy a vendég épp hol van.
    dtStartLine = `DTSTART;TZID=Europe/Budapest:${dateStr}T${hh}${mm}00`;
    dtEndOrDurationLine = 'DURATION:PT4H';
  } else {
    // Nincs megadott pontos időpont: egész napos eseményként adjuk hozzá.
    const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1));
    const nextStr = `${next.getUTCFullYear()}${pad2(next.getUTCMonth() + 1)}${pad2(next.getUTCDate())}`;
    dtStartLine = `DTSTART;VALUE=DATE:${dateStr}`;
    dtEndOrDurationLine = `DTEND;VALUE=DATE:${nextStr}`;
  }

  const now = new Date();
  const dtStamp = `${now.getUTCFullYear()}${pad2(now.getUTCMonth() + 1)}${pad2(now.getUTCDate())}T`
    + `${pad2(now.getUTCHours())}${pad2(now.getUTCMinutes())}${pad2(now.getUTCSeconds())}Z`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Wedigo//RSVP//HU',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:wedigo-event-${eventData.id}@wedigo.hu`,
    `DTSTAMP:${dtStamp}`,
    dtStartLine,
    dtEndOrDurationLine,
    `SUMMARY:${escapeIcsText(eventData.name)}`,
    ...(location ? [`LOCATION:${escapeIcsText(location)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.join('\r\n');
}

function handleAddToCalendar(eventData) {
  const ics = buildInvitationIcs(eventData);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(eventData.name || 'esemeny').replace(/[^\p{L}\p{N}]+/gu, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function RSVPPage() {
  const { token } = useParams();
  const theme = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const [form, setForm] = useState({
    rsvpStatus: '',
    dietaryRestrictions: '',
    guestNotes: '',
    plusOneName: '',
  });

  useEffect(() => {
    loadRSVP();
  }, [token]);

  const loadRSVP = async () => {
    setLoading(true);
    try {
      const res = await apiGet(API_ENDPOINTS.RSVP.GET(token));
      if (res.success) {
        setData(res.data);
        setForm({
          rsvpStatus: res.data.guest.rsvpStatus || '',
          dietaryRestrictions: res.data.guest.dietaryRestrictions || '',
          guestNotes: res.data.guest.guestNotes || '',
          plusOneName: res.data.guest.plusOneName || '',
        });
        if (res.data.guest.rsvpStatus && res.data.guest.rsvpStatus !== 'NO_RESPONSE') {
          setSubmitted(true);
        }
      } else {
        setError(res.error || 'Érvénytelen vagy lejárt meghívó link.');
      }
    } catch {
      setError('Hiba a betöltés során. Kérjük, próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.rsvpStatus) {
      setSnack({ open: true, msg: 'Kérjük, jelezze vissza részvételét!', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await apiPost(API_ENDPOINTS.RSVP.SUBMIT(token), form);
      if (res.success) {
        setSubmitted(true);
        setEditing(false);
        setSnack({ open: true, msg: 'Visszajelzés elmentve!', severity: 'success' });
        await loadRSVP();
      } else {
        setSnack({ open: true, msg: res.error || 'Mentési hiba', severity: 'error' });
      }
    } catch {
      setSnack({ open: true, msg: 'Hiba a küldés során', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f0' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 480, borderRadius: '12px' }}>
          {error || 'Érvénytelen meghívó link.'}
        </Alert>
      </Box>
    );
  }

  if (data.invitation && !data.invitation.isActive) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Alert severity="info" sx={{ maxWidth: 480, borderRadius: '12px' }}>
          Ez a meghívó már nem aktív.
        </Alert>
      </Box>
    );
  }

  const { guest, event: eventData, invitation } = data;
  const guestName = `${guest.firstName} ${guest.lastName}`;
  const allowPlusOne = invitation?.allowPlusOne ?? true;
  const showForm = !submitted || editing;

  // Dinamikus stílus és szöveg — null esetén alapértéket használ
  const invStyle = {
    bgColor: invitation?.bgColor || STYLE_DEFAULTS.bgColor,
    textColor: invitation?.textColor || STYLE_DEFAULTS.textColor,
    accentColor: invitation?.accentColor || STYLE_DEFAULTS.accentColor,
    fontFamily: invitation?.fontFamily || STYLE_DEFAULTS.fontFamily,
    headingSize: invitation?.headingSize || STYLE_DEFAULTS.headingSize,
    bodySize: invitation?.bodySize || STYLE_DEFAULTS.bodySize,
  };
  const invTexts = {
    greetingText: invitation?.greetingText || TEXT_DEFAULTS.greetingText,
    rsvpSectionTitle: invitation?.rsvpSectionTitle || TEXT_DEFAULTS.rsvpSectionTitle,
    attendingLabel: invitation?.attendingLabel || TEXT_DEFAULTS.attendingLabel,
    declineLabel: invitation?.declineLabel || TEXT_DEFAULTS.declineLabel,
    maybeLabel: invitation?.maybeLabel || TEXT_DEFAULTS.maybeLabel,
    submitLabel: invitation?.submitLabel || TEXT_DEFAULTS.submitLabel,
  };

  const RSVP_OPTIONS = [
    { value: 'ATTENDING', label: invTexts.attendingLabel, icon: <CheckIcon />, color: 'success' },
    { value: 'NOT_ATTENDING', label: invTexts.declineLabel, icon: <CancelIcon />, color: 'error' },
    { value: 'MAYBE', label: invTexts.maybeLabel, icon: <HelpIcon />, color: 'warning' },
  ];

  // Betűtípus dinamikus betöltése
  loadGoogleFont(invStyle.fontFamily);

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: invStyle.bgColor,
      backgroundImage: `radial-gradient(circle at 20% 80%, ${alpha(invStyle.accentColor, 0.08)} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(invStyle.accentColor, 0.06)} 0%, transparent 50%)`,
      py: { xs: 3, sm: 6 },
      fontFamily: `'${invStyle.fontFamily}', serif`,
      color: invStyle.textColor,
    }}>
      <Container maxWidth="sm">

        {/* Fejléc */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: `'${invStyle.fontFamily}', serif`,
              fontStyle: 'italic',
              color: invStyle.accentColor,
              fontSize: invStyle.headingSize,
              mb: 0.5,
            }}
          >
            {eventData.name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {eventData.eventDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon sx={{ fontSize: 16, color: invStyle.textColor, opacity: 0.6 }} />
                <Typography sx={{ fontSize: invStyle.bodySize - 2, color: invStyle.textColor, opacity: 0.7 }}>
                  {format(new Date(eventData.eventDate), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
                </Typography>
              </Box>
            )}
            {eventData.venueName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlaceIcon sx={{ fontSize: 16, color: invStyle.textColor, opacity: 0.6 }} />
                <Typography sx={{ fontSize: invStyle.bodySize - 2, color: invStyle.textColor, opacity: 0.7 }}>
                  {eventData.venueName}{eventData.venueCity ? `, ${eventData.venueCity}` : ''}
                </Typography>
              </Box>
            )}
          </Box>
          {eventData.eventDate && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
                onClick={() => handleAddToCalendar(eventData)}
                sx={{ color: invStyle.accentColor, textTransform: 'none' }}
              >
                Hozzáadás a naptárhoz
              </Button>
            </Box>
          )}
        </Box>

        {/* Meghívó kép */}
        {invitation?.imageUrl && (
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            {invitation.imageType === 'pdf' ? (
              <Button variant="outlined" color="secondary" href={invitation.imageUrl} target="_blank" rel="noopener noreferrer">
                Meghívó megtekintése (PDF)
              </Button>
            ) : (
              <Box
                component="img"
                src={invitation.imageUrl}
                alt="Meghívó"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 480,
                  borderRadius: '12px',
                  boxShadow: `0 8px 32px ${alpha(invStyle.accentColor, 0.18)}`,
                }}
              />
            )}
          </Box>
        )}

        {/* Személyes üdvözlés */}
        <Paper elevation={0} sx={{
          borderRadius: '20px',
          border: `1px solid ${alpha(invStyle.accentColor, 0.25)}`,
          overflow: 'hidden',
          mb: 3,
          bgcolor: alpha('#fff', 0.6),
        }}>
          <Box sx={{
            height: 4,
            background: `linear-gradient(90deg, ${invStyle.accentColor} 0%, ${alpha(invStyle.accentColor, 0.4)} 100%)`,
          }} />

          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: `'${invStyle.fontFamily}', serif`,
                color: invStyle.accentColor,
                fontSize: invStyle.headingSize * 0.6,
                mb: 0.5,
              }}
            >
              Üdvözlünk, {guestName}!
            </Typography>
            <Typography sx={{ fontSize: invStyle.bodySize, color: invStyle.textColor, opacity: 0.75, mb: 2, lineHeight: 1.7 }}>
              {invTexts.greetingText}
            </Typography>

            {invitation?.customMessage && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  sx={{ fontStyle: 'italic', color: invStyle.textColor, opacity: 0.7, lineHeight: 1.8, fontSize: invStyle.bodySize }}
                >
                  {invitation.customMessage}
                </Typography>
              </>
            )}

            {invitation?.rsvpDeadline && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Kérjük, jelezzen vissza <strong>
                  {format(new Date(invitation.rsvpDeadline), 'yyyy. MMMM d.', { locale: hu })}
                </strong>-ig.
              </Alert>
            )}
          </Box>
        </Paper>

        {/* Visszajelzés elküldve állapot */}
        {submitted && !editing && (
          <Paper elevation={0} sx={{
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.light, 0.08),
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            textAlign: 'center',
          }}>
            <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700, mb: 0.5 }}>
              Visszajelzés elküldve!
            </Typography>
            <Typography sx={{ fontSize: invStyle.bodySize, color: invStyle.textColor, mb: 2 }}>
              Visszajelzése: <strong>
                {RSVP_OPTIONS.find(o => o.value === guest.rsvpStatus)?.label || guest.rsvpStatus}
              </strong>
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              size="small"
            >
              Módosítás
            </Button>
          </Paper>
        )}

        {/* RSVP Form */}
        {showForm && (
          <Paper elevation={0} sx={{
            borderRadius: '16px',
            border: `1px solid ${alpha(invStyle.accentColor, 0.15)}`,
            p: { xs: 2.5, sm: 3 },
            bgcolor: alpha('#fff', 0.5),
          }}>
            <Typography sx={{ fontWeight: 700, color: invStyle.accentColor, fontSize: invStyle.bodySize + 1, mb: 2 }}>
              {invTexts.rsvpSectionTitle}
            </Typography>

            {/* RSVP toggle gombok */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {RSVP_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  variant={form.rsvpStatus === opt.value ? 'contained' : 'outlined'}
                  color={opt.color}
                  startIcon={opt.icon}
                  onClick={() => setForm(f => ({ ...f, rsvpStatus: opt.value }))}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.25,
                    borderRadius: '10px',
                    fontWeight: form.rsvpStatus === opt.value ? 700 : 500,
                    fontSize: invStyle.bodySize,
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Kísérő */}
            {allowPlusOne && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: invStyle.bodySize }}>
                  Kísérő neve (opcionális)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Pl. Kiss Ádám"
                  value={form.plusOneName}
                  onChange={e => setForm(f => ({ ...f, plusOneName: e.target.value }))}
                />
              </Box>
            )}

            {/* Ételallergia */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: invStyle.bodySize }}>
                Ételallergia / diétás igény (opcionális)
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Pl. gluténmentes, laktózmentes..."
                value={form.dietaryRestrictions}
                onChange={e => setForm(f => ({ ...f, dietaryRestrictions: e.target.value }))}
              />
            </Box>

            {/* Saját megjegyzés */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: invStyle.bodySize }}>
                Megjegyzés (opcionális)
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Egyéb megjegyzés a szervezőknek..."
                value={form.guestNotes}
                onChange={e => setForm(f => ({ ...f, guestNotes: e.target.value }))}
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={saving || !form.rsvpStatus}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
              sx={{
                borderRadius: '10px', py: 1.5, fontWeight: 700,
                fontSize: invStyle.bodySize,
                bgcolor: invStyle.accentColor,
                '&:hover': { bgcolor: alpha(invStyle.accentColor, 0.85) },
              }}
            >
              {saving ? 'Küldés...' : submitted ? 'Módosítás mentése' : invTexts.submitLabel}
            </Button>
          </Paper>
        )}

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="caption" color="text.disabled">
            Wedigo Esküvőtervező
          </Typography>
        </Box>

      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
